import { cn } from "../../classNames-DkX30VJU.js";
import { Tooltip, TooltipProvider, splitEntry } from "../../utils-DGd7lPd6.js";
import { PanelRightClose, PanelRightOpen, Search } from "../../icons-DDR9dFSr.js";
import { Button } from "../../Button-i0ITjN_1.js";
import { useControllableState } from "../../useControllableState-BCvUOC6G.js";
import { forwardAccessibleName } from "../utils/accessibleName.js";
import { Combobox as Combobox$1, ComboboxClearButton, ComboboxOpenButton, ComboboxOptions, createCreatableItem, defaultGroupToLabel, defaultItemToDisabled, defaultItemToIcon, defaultQueryToCreateLabel, defaultRowLabel, defaultToCreateValue, flatItems, isCreatableItem, isGroupHeaderItem, isSelectAllItem, sanitizeTypedInput, selectAllItem, toEntries, toLabel, useCreatableStringValue, useGroupHeaders, useItemEquality, useQueryReporter, useVirtualList, useWithCreated } from "../../Combobox-DdFtEumY.js";
import { useCallback, useMemo, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Combobox } from "@base-ui/react/combobox";
// src/blocks/MultiCombobox/MultiCombobox.tsx
/** Joins the create row's values with the entry's separator; a `RegExp` uses the default one. */
const joinCreateParts = (parts, separator) => parts.join(`${typeof separator === "string" ? separator : ","} `);
/**
* White group cards on a grey list: each group's row and open items sit on one card.
*
* - The grey is `--secondary` at 45% over the popover, about 1.10:1 against white.
* - The gap is space under each card's last row, so a windowed list measures it with the row.
* - Base UI's `role="group"` wraps a group's row and items in either list.
* - Rows are square; the card's first and last rows take its corners. Enabled rows show a pointer.
*/
const GROUP_BANDED = [
	"tw:bg-[color-mix(in_oklab,rgb(var(--secondary))_45%,rgb(var(--popover)))]",
	"tw:[&_[role=listbox]]:bg-[color-mix(in_oklab,rgb(var(--secondary))_45%,rgb(var(--popover)))]",
	"tw:[&_[role=group]]:relative tw:[&_[role=group]]:isolate",
	"tw:[&_[role=group]]:before:absolute tw:[&_[role=group]]:before:inset-x-0 tw:[&_[role=group]]:before:top-0",
	"tw:[&_[role=group]]:before:bottom-2 tw:[&_[role=group]]:before:-z-10",
	"tw:[&_[role=group]]:before:rounded-lg tw:[&_[role=group]]:before:bg-popover",
	"tw:[&_[role=group]>[role=option]:last-child]:mb-2 tw:[&_[data-group-last]]:pb-2",
	"tw:[&_[role=group]_[role=option]]:rounded-none",
	"tw:[&_[role=group]>:first-child_[role=option]]:rounded-t-lg",
	"tw:[&_[role=group]>:last-child_[role=option]]:rounded-b-lg",
	"tw:[&_[role=option]:not([data-disabled]):not([data-locked])]:cursor-pointer",
	"tw:[&_[role=option]]:items-start",
	"tw:[&_[role=option]>:not([data-slot=combobox-item-label]):not([data-slot=combobox-group-toggle])]:mt-0.5",
	"tw:[&_[role=option]>[data-slot=combobox-group-toggle]]:-mt-0.5"
].join(" ");
const NO_ITEMS = [];
const NO_VALUES = [];
/**
* Searchable multi-select with chips, built on Base UI Combobox.
*
* @see RECIPES.md#creatable-multi-combobox
* @see RECIPES.md#filter-toolbar
*/
const MultiCombobox = ({ items: _itemsProp, value: _valueProp, defaultValue, onValueChange, itemToStringLabel, itemToStringValue, disabled, filter: filterProp, isItemEqualToValue: isItemEqualToValueProp, inputRef, focusVariant, state, placeholder, allowClear = true, name, onBlur, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-required": ariaRequired, className, allowSelectAll = false, empty = "No items", isLoading = false, indicator, virtualized = false, inputValueSanitizer, onQueryChange, allowCreate, canCreate, queryToCreateValue, queryToCreateLabel = defaultQueryToCreateLabel, createSeparator = ",", itemToDisabled = defaultItemToDisabled, itemToLocked, itemToLabel: itemToLabelProp, itemToIcon = defaultItemToIcon, groupToLabel = defaultGroupToLabel, collapsibleGroups = false, selectableGroups = false, collapseSelectedGroups = false, groupAppearance = "plain", itemToChipLabel, itemToChipColor, pinnedChipLabels, renderChips, renderAside, asideOpen: asideOpenProp, onAsideOpenChange, defaultOpen = false, onOpenChange, anchor, query: queryProp, maxVisible = 3, ...accessibleName }) => {
	const itemsProp = _itemsProp ?? NO_ITEMS;
	const [valueProp, setValueProp] = useControllableState({
		prop: _valueProp === null ? NO_VALUES : _valueProp,
		defaultProp: defaultValue ?? NO_VALUES
	});
	const itemToString = useCallback((item) => itemToStringLabel?.(item) ?? toLabel(item), [itemToStringLabel]);
	const itemToLabel = itemToLabelProp ?? defaultRowLabel(itemToStringLabel);
	const [inputValue, setInputValue] = useState("");
	const [asideOpen, setAsideOpen] = useControllableState({
		prop: asideOpenProp,
		defaultProp: true,
		onChange: onAsideOpenChange
	});
	const [lastHighlighted, setLastHighlighted] = useState(null);
	const [createdItems, setCreatedItems] = useState([]);
	const query = (queryProp ?? inputValue).trim();
	useQueryReporter(query, onQueryChange);
	const busy = isLoading || void 0;
	const ownsInput = Boolean(queryProp !== void 0 || allowCreate || inputValueSanitizer || onQueryChange || itemToLabelProp || collapsibleGroups || selectableGroups);
	const baseItems = useWithCreated(itemsProp, createdItems);
	const allFlatItems = flatItems(baseItems);
	const sampleItem = allFlatItems[0];
	const hasSelectAllRow = allowSelectAll && allFlatItems.length > 0;
	const isAllSelected = hasSelectAllRow && allFlatItems.length === (valueProp?.length ?? 0);
	const defaultFilter = Combobox.useFilter({
		multiple: true,
		value: valueProp
	});
	const wrappedItemToStringLabel = useMemo(() => {
		if (!allowCreate && !collapsibleGroups && !selectableGroups) return itemToStringLabel && itemToString;
		return (item) => {
			if (isCreatableItem(item)) return item.value;
			if (isGroupHeaderItem(item)) return item.label;
			return itemToString(item);
		};
	}, [
		allowCreate,
		collapsibleGroups,
		selectableGroups,
		itemToStringLabel,
		itemToString
	]);
	const itemFilter = filterProp ?? defaultFilter.contains;
	const isItemEqualToValue = useItemEquality(isItemEqualToValueProp);
	const groupHeaders = useGroupHeaders({
		items: itemsProp,
		collapsible: collapsibleGroups,
		selectable: selectableGroups,
		query,
		matches: (item) => itemFilter(item, query, wrappedItemToStringLabel),
		isItemEqualToValue,
		startsCollapsed: collapseSelectedGroups ? (group) => group.items.length > 0 && group.items.every((item) => valueProp.some((selected) => isItemEqualToValue(item, selected))) : void 0,
		startsOpen: defaultOpen
	});
	const listItems = useWithCreated(groupHeaders.items, createdItems);
	const virtualList = useVirtualList(baseItems, virtualized);
	const labelKey = (item) => itemToString(item).toLowerCase();
	const createParts = !allowCreate || disabled || !query ? [] : splitEntry(query, createSeparator);
	const selectedKeys = /* @__PURE__ */ new Set();
	if (createParts.length > 0) for (const item of valueProp) selectedKeys.add(labelKey(item));
	const itemKeys = /* @__PURE__ */ new Set();
	if (createParts.length > 0 && canCreate) for (const item of allFlatItems) itemKeys.add(labelKey(item));
	const namesAnItem = (key) => canCreate ? itemKeys.has(key) : allFlatItems.some((item) => labelKey(item) === key);
	/**
	* The entry's values not yet selected, minus those `canCreate` rejects.
	*
	* - Both names the create row and is exactly what committing it adds.
	* - A value naming an existing item counts and bypasses `canCreate` — committing selects it.
	*/
	const addedCreateParts = createParts.filter((part) => {
		const key = part.toLowerCase();
		if (selectedKeys.has(key)) return false;
		return !canCreate || namesAnItem(key) || canCreate(part);
	});
	const soleValueKey = createParts.length === 1 ? createParts[0].toLowerCase() : void 0;
	const showCreateRow = addedCreateParts.length > 0 && (soleValueKey === void 0 || !namesAnItem(soleValueKey));
	const items = useMemo(() => {
		const withSelectAll = hasSelectAllRow ? [...toEntries(listItems, [selectAllItem]), ...listItems] : listItems;
		return showCreateRow ? [...withSelectAll, ...toEntries(listItems, [createCreatableItem(query)])] : withSelectAll;
	}, [
		hasSelectAllRow,
		showCreateRow,
		listItems,
		query
	]);
	const filterHeader = groupHeaders.enabled ? groupHeaders.filterHeader : void 0;
	const filter = useCallback((item, query, itemToString) => {
		if (isSelectAllItem(item)) return query.trim() === "";
		if (isCreatableItem(item)) return true;
		return filterHeader?.(item) ?? itemFilter(item, query, itemToString);
	}, [filterHeader, itemFilter]);
	const wrappedItemToStringValue = useCreatableStringValue(itemToStringValue, allowCreate);
	const value = isAllSelected ? [...valueProp, selectAllItem] : valueProp;
	const selectedSet = selectableGroups && !isItemEqualToValueProp ? new Set(valueProp) : void 0;
	const isSelected = (item) => selectedSet?.has(item) ?? valueProp.some((selected) => isItemEqualToValue(item, selected));
	/** What a group's row selects: the group's items the query matches, the disabled aside. */
	const groupTargets = (row) => groupHeaders.headerOf(row)?.matching.filter((item) => !itemToDisabled(item) && !itemToLocked?.(item)) ?? [];
	/**
	* Replaces a pressed group row in `next` with its items.
	*
	* - Base UI adds the row to `value` like any unselected item.
	* - A row whose items are all selected deselects them.
	*/
	const selectGroupRow = (next) => {
		const row = next.find(isGroupHeaderItem);
		if (row === void 0) return next;
		const rest = next.filter((item) => item !== row);
		const targets = groupTargets(row);
		if (!targets.every(isSelected)) return [...rest, ...targets.filter((item) => !isSelected(item))];
		return rest.filter((item) => !targets.some((target) => isItemEqualToValue(target, item)));
	};
	const groupRowChecked = (targets) => {
		const selectedCount = targets.filter(isSelected).length;
		if (selectedCount === 0) return false;
		return selectedCount === targets.length ? true : "mixed";
	};
	const handleInputValueChange = (next, { reason }) => {
		if (queryProp !== void 0) return;
		setInputValue(sanitizeTypedInput(next, reason, inputValueSanitizer));
	};
	const handleValueChange = (next, event) => {
		if (ownsInput) setInputValue((prev) => prev === "" ? prev : "");
		if (!onValueChange && _valueProp !== void 0) return;
		const picked = selectGroupRow(next);
		const created = [];
		const converted = (() => {
			if (!allowCreate || !picked.some(isCreatableItem)) return picked;
			const itemsByLabel = /* @__PURE__ */ new Map();
			for (const item of allFlatItems) itemsByLabel.set(labelKey(item), item);
			return picked.flatMap((item) => {
				if (!isCreatableItem(item)) return [item];
				return addedCreateParts.map((part) => {
					const existing = itemsByLabel.get(part.toLowerCase());
					if (existing !== void 0) return existing;
					const value = defaultToCreateValue(part, sampleItem, queryToCreateValue);
					created.push(value);
					return value;
				});
			});
		})();
		const hasSelectAll = converted.some((item) => isSelectAllItem(item));
		const newValue = (() => {
			if (!hasSelectAllRow) return converted;
			if (!hasSelectAll) return isAllSelected ? [] : converted;
			return isAllSelected ? converted.filter((item) => !isSelectAllItem(item)) : allFlatItems;
		})();
		if (allowCreate) {
			const selected = new Set(newValue.map(itemToString));
			setCreatedItems((prev) => [...prev, ...created].filter((item) => selected.has(itemToString(item))));
		}
		const kept = itemToLocked ? [...newValue.filter((item) => !itemToLocked(item) || isSelected(item)), ...valueProp.filter((item) => itemToLocked(item) && !newValue.some((picked) => isItemEqualToValue(picked, item)))] : newValue;
		if (itemToLocked && kept.length === valueProp.length && kept.every(isSelected)) return;
		setValueProp(kept);
		onValueChange?.(kept, event);
	};
	const toHighlighted = (item) => {
		if (item === void 0 || isCreatableItem(item) || isSelectAllItem(item)) return null;
		const header = groupHeaders.headerOf(item);
		if (header) return {
			kind: "group",
			group: header.group
		};
		return isGroupHeaderItem(item) ? null : {
			kind: "item",
			item
		};
	};
	const options = /* @__PURE__ */ jsx(ComboboxOptions, {
		isLoading,
		empty,
		virtualized: virtualList.enabled,
		listRef: virtualList.ref,
		groupHeaders,
		groupToLabel,
		indicator,
		itemToLabel: (item) => itemToLabel(item, query),
		itemToIcon,
		itemToDisabled,
		itemToLocked,
		createLabel: () => queryToCreateLabel(joinCreateParts(addedCreateParts, createSeparator)),
		groupRowState: selectableGroups ? (row) => {
			const targets = groupTargets(row);
			const shown = (groupHeaders.headerOf(row)?.matching ?? []).filter((item) => !itemToDisabled(item) || isSelected(item));
			const locked = targets.length === 0 && shown.some((item) => itemToLocked?.(item));
			return {
				checked: groupRowChecked(shown),
				disabled: targets.length === 0 && !locked,
				locked
			};
		} : void 0
	});
	const input = /* @__PURE__ */ jsx(Combobox$1.Input, {
		...forwardAccessibleName(accessibleName),
		name,
		onBlur,
		"aria-describedby": ariaDescribedBy,
		"aria-invalid": ariaInvalid,
		"aria-required": ariaRequired,
		"aria-busy": busy,
		placeholder,
		disabled,
		tabIndex: anchor && queryProp !== void 0 ? -1 : void 0,
		onKeyDown: groupHeaders.onInputKeyDown,
		className: anchor ? "tw:min-w-0 tw:grow tw:!px-0 tw:!py-0" : "tw:w-auto tw:min-w-0 tw:grow tw:basis-[120px] tw:!px-0 tw:!py-0"
	});
	const popupSearch = anchor && queryProp === void 0 ? /* @__PURE__ */ jsx("div", {
		className: "tw:flex tw:items-center tw:gap-2 tw:px-3 tw:pt-2.5 tw:pb-1",
		children: /* @__PURE__ */ jsxs(Combobox$1.InputGroup, {
			className: "tw:h-9 tw:flex-1 tw:gap-2 tw:bg-background tw:px-3",
			children: [/* @__PURE__ */ jsx(Search, { className: "tw:size-4 tw:shrink-0 tw:text-muted-foreground" }), input]
		})
	}) : null;
	return /* @__PURE__ */ jsxs(Combobox$1.Root, {
		multiple: true,
		autoHighlight: true,
		items,
		value,
		filter,
		isItemEqualToValue,
		onValueChange: handleValueChange,
		defaultOpen,
		onOpenChange: (open) => {
			groupHeaders.onOpenChange(open);
			if (!open) setLastHighlighted(null);
			onOpenChange?.(open);
		},
		onItemHighlighted: (item, details) => {
			groupHeaders.onItemHighlighted(item);
			virtualList.onItemHighlighted(details);
			const next = renderAside ? toHighlighted(item) : null;
			if (next) setLastHighlighted(next);
		},
		virtualized: virtualList.enabled,
		itemToStringLabel: wrappedItemToStringLabel,
		itemToStringValue: wrappedItemToStringValue,
		disabled,
		inputRef,
		...ownsInput ? {
			inputValue: queryProp ?? inputValue,
			onInputValueChange: handleInputValueChange
		} : {},
		children: [
			anchor && queryProp !== void 0 && /* @__PURE__ */ jsx("div", {
				className: "tw:sr-only",
				children: input
			}),
			!anchor && /* @__PURE__ */ jsx(Combobox$1.InputGroup, {
				state,
				focusVariant,
				className,
				children: /* @__PURE__ */ jsxs(Combobox.Chips, {
					className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5 tw:px-4 tw:py-1.5",
					children: [/* @__PURE__ */ jsx(Combobox$1.Chips, {
						maxVisible,
						variant: "pill",
						itemToChipLabel: itemToChipLabel ?? ((item) => itemToLabel(item, "")),
						itemToChipColor,
						pinnedChipLabels,
						children: renderChips
					}), /* @__PURE__ */ jsxs("div", {
						className: "tw:flex tw:min-w-0 tw:grow tw:items-center tw:gap-1",
						children: [
							input,
							allowClear && /* @__PURE__ */ jsx(ComboboxClearButton, { disabled }),
							/* @__PURE__ */ jsx(ComboboxOpenButton, {
								disabled,
								isLoading
							})
						]
					})]
				})
			}),
			renderAside ? /* @__PURE__ */ jsxs(Combobox$1.Content, {
				anchor,
				className: cn("tw:@container tw:w-[max(var(--anchor-width),48rem)]", "tw:min-h-[min(var(--available-height),22rem)] tw:data-empty:min-h-0", anchor && "tw:max-h-[min(var(--available-height),32rem)]", groupAppearance === "banded" && GROUP_BANDED),
				children: [
					popupSearch,
					/* @__PURE__ */ jsxs("div", {
						className: cn("tw:grid tw:min-h-0 tw:flex-1 tw:grid-rows-[minmax(0,1fr)]", "tw:motion-safe:transition-[grid-template-columns] tw:motion-safe:duration-200 tw:motion-safe:ease-out", asideOpen ? "tw:grid-cols-[minmax(0,2fr)_1fr]" : "tw:grid-cols-[minmax(0,2fr)_0fr]", "tw:@max-[40rem]:grid-cols-[minmax(0,2fr)_0fr]"),
						children: [/* @__PURE__ */ jsx("div", {
							className: cn("tw:flex tw:min-h-0 tw:min-w-0 tw:flex-col tw:group-data-empty/combobox-content:col-span-2", !asideOpen && "tw:pb-10 tw:@max-[40rem]:pb-0 tw:group-data-empty/combobox-content:pb-0"),
							children: options
						}), /* @__PURE__ */ jsx("div", {
							className: cn("tw:relative tw:min-w-0 tw:overflow-hidden tw:border-border", "tw:motion-safe:transition-opacity tw:motion-safe:duration-200", asideOpen ? "tw:border-l tw:opacity-100" : "tw:opacity-0", "tw:@max-[40rem]:invisible tw:@max-[40rem]:border-l-0 tw:@max-[40rem]:opacity-0", "tw:group-data-empty/combobox-content:hidden"),
							"data-slot": "combobox-inspector",
							inert: !asideOpen,
							children: /* @__PURE__ */ jsx("div", {
								className: "tw:absolute tw:inset-y-0 tw:left-0 tw:w-full tw:min-w-60 tw:overflow-y-auto tw:p-4 tw:pb-12",
								children: renderAside(lastHighlighted)
							})
						})]
					}),
					/* @__PURE__ */ jsx("div", {
						className: "tw:absolute tw:right-2 tw:bottom-2 tw:@max-[40rem]:hidden tw:group-data-empty/combobox-content:hidden",
						children: /* @__PURE__ */ jsx(AsideToggle, {
							open: asideOpen,
							onOpenChange: setAsideOpen
						})
					})
				]
			}) : /* @__PURE__ */ jsxs(Combobox$1.Content, {
				anchor,
				className: cn(anchor && "tw:max-h-[min(var(--available-height),32rem)]", groupAppearance === "banded" && GROUP_BANDED),
				children: [popupSearch, options]
			})
		]
	});
};
/**
* Hides or shows the inspector beside the list, from the popup's bottom-right corner.
*
* - Floats over the list while the inspector is hidden; gone with it when the popup is too narrow.
* - Keeps the input focused, so the popup stays open.
*/
const AsideToggle = ({ open, onOpenChange }) => {
	const label = open ? "Hide inspector" : "Show inspector";
	const Icon = open ? PanelRightClose : PanelRightOpen;
	return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs(Tooltip.Root, { children: [/* @__PURE__ */ jsx(Tooltip.Trigger, {
		asChild: true,
		children: /* @__PURE__ */ jsx(Button, {
			variant: "outline",
			size: "icon-xs",
			"aria-label": label,
			"aria-expanded": open,
			className: "tw:bg-popover tw:shadow-sm",
			onMouseDown: (event) => event.preventDefault(),
			onClick: () => onOpenChange(!open),
			children: /* @__PURE__ */ jsx(Icon, {})
		})
	}), /* @__PURE__ */ jsx(Tooltip.Content, {
		placement: "left",
		children: label
	})] }) });
};
export { MultiCombobox };

//# sourceMappingURL=MultiCombobox.js.map